import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Target, Users, Rocket, Shield, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompanyOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/financial/company-overview`).then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading || !data) return <div className="h-64 skeleton-shimmer rounded" />;

  return (
    <div className="space-y-5" data-testid="company-overview-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">{data.name}</h1>
          <p className="text-sm text-slate-400 mt-1">{data.tagline}</p>
        </div>
        {data.im_pdf_url && (
          <a href={data.im_pdf_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs text-blue-400 hover:text-blue-300 border border-blue-800 bg-blue-950/30 px-3 py-1.5 rounded-md transition-colors"
            data-testid="download-im-btn">
            <ExternalLink className="w-3.5 h-3.5" /> Investment Memorandum (PDF)
          </a>
        )}
      </div>

      {/* Vision & Fundraise */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="glass-card rounded-md p-5 border-l-2 border-blue-500">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Vision</p>
          <p className="text-sm text-slate-200">{data.vision}</p>
        </div>
        <div className="glass-card rounded-md p-5 border-l-2 border-emerald-500">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Current Fundraise</p>
          <p className="text-sm text-emerald-400 font-semibold">{data.fundraise}</p>
          <p className="text-[11px] text-slate-500 mt-1">Investment to date: {data.investment_to_date}</p>
        </div>
      </div>

      {/* Key Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        {Object.entries(data.key_stats || {}).map(([key, val]) => (
          <div key={key} className="glass-card rounded-md p-3">
            <p className="text-[9px] text-slate-500 uppercase tracking-wider">{key.replace(/_/g, " ")}</p>
            <p className="font-mono text-sm text-slate-200 mt-0.5">{val}</p>
          </div>
        ))}
      </div>

      {/* Market Opportunity */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="market-opportunity">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
            <Target className="w-4 h-4 text-amber-400" /> Market Opportunity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(data.market_opportunity || {}).map(([key, val]) => (
              <div key={key} className="p-3 bg-slate-800/30 rounded-md">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider">{key.replace(/_/g, " ")}</p>
                <p className="font-mono text-xs text-amber-400 mt-1">{val}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Competitive Advantages */}
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="competitive-advantages">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-400" /> Competitive Advantages
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data.competitive_advantages || []).map((adv, i) => (
              <div key={i} className="flex gap-2.5 text-xs text-slate-300">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                {adv}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Growth Initiatives */}
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="growth-initiatives">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <Rocket className="w-4 h-4 text-violet-400" /> Growth Initiatives
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data.growth_initiatives || []).map((init, i) => (
              <div key={i} className="flex gap-2.5 text-xs text-slate-300">
                <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-1.5 flex-shrink-0" />
                {init}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Channel Mix & Founders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="channel-mix">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-400" /> Channel Mix (FY25)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {Object.entries(data.channel_mix_fy25 || {}).map(([ch, pct]) => (
              <div key={ch} className="flex items-center gap-3">
                <span className="text-xs text-slate-300 w-24">{ch}</span>
                <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: pct }} />
                </div>
                <span className="font-mono text-xs text-slate-400 w-10 text-right">{pct}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="founders">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-400" /> Founders
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(data.founders || []).map((f, i) => (
              <div key={i} className="glass-card rounded-md p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-slate-200">{f.name}</span>
                  <Badge variant="outline" className="text-[9px] text-blue-400 border-blue-800">{f.role}</Badge>
                </div>
                <p className="text-xs text-slate-400">{f.background}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
